import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Bot,
  Building2,
  CalendarDays,
  Check,
  ClipboardCheck,
  FileText,
  GraduationCap,
  Languages,
  LockKeyhole,
  Presentation,
  ShieldCheck,
  UsersRound,
  WalletCards,
} from "lucide-react";
import {
  ContactSalesModal,
  MarketingTopBar,
  SiteFooter,
} from "../components/marketing/shared";
import MarketingAssetSlot from "../components/marketing/MarketingAssetSlot";
import { MarketingMotion, useMarketingMotion } from "../components/marketing/MarketingMotion";
import { ScrollProgressBar } from "../components/marketing/scrollMotion";
import MarketingSeo, { seoOrigin } from "../components/marketing/MarketingSeo";
import BrandWordmarkText from "../components/BrandWordmarkText";
import { CTA_PRIMARY_LABEL } from "../components/marketing/navigation";
import { track } from "../lib/analytics";

const EASE = [0.16, 1, 0.3, 1];

export const MOBILE_PERSONAS = Object.freeze([
  {
    key: "student", assetKey: "screenNotices", title: "Student", label: "See the school update that matters",
    description: "Pinned circulars and clearly labelled notices help students separate the next relevant update from the wider school feed.",
    proofs: ["Scan pinned, event and holiday notices in one mobile view.", "Open updates within the audience set by the school."], icon: GraduationCap,
  },
  {
    key: "guardian", assetKey: "screenAira", title: "Parent or guardian", label: "Ask about the school day in context",
    description: "Aira gives families a focused place to ask about fees, attendance, marks and transport before taking the next step.",
    proofs: ["Use quick prompts or type a question in the family workspace.", "Double-check important details before acting on an answer."], icon: UsersRound,
  },
  {
    key: "teacher", assetKey: "screenTaskDetail", title: "Staff", label: "Complete accountable work away from a desk",
    description: "Task details keep the checklist, photo or gallery proof controls and completion note together for staff in the field.",
    proofs: ["Work through a typed checklist with clear completion state.", "Attach permitted evidence and submit when the task is ready."], icon: Presentation,
  },
  {
    key: "admin", assetKey: "screenSecurityOverview", title: "School leader", label: "Keep account controls visible",
    description: "Password, authenticator and notification-device controls make the signed-in account’s protection state easier to review.",
    proofs: ["Review protection status without navigating a desktop settings tree.", "Open the relevant control and confirm changes deliberately."], icon: Building2,
  },
]);

export const MOBILE_MODULES = Object.freeze([
  {
    key: "aira", assetKey: "screenAira", title: "Aira assistant", role: "Parent or guardian", group: "featured", icon: Bot,
    description: "Ask child-scoped questions about fees, attendance, marks or transport by typing or speaking.",
    evidence: "Quick prompts begin common questions, while the interface asks families to double-check important details before acting.",
  },
  {
    key: "operations-tasks", assetKey: "screenTaskDetail", title: "Operations tasks", role: "Staff and transport crew", group: "featured", icon: ClipboardCheck,
    description: "Priority tasks combine checklists, proof capture, optional location and completion notes in one focused view.",
    evidence: "The detail screen keeps the task brief, checklist and permitted evidence controls together before submission.",
  },
  {
    key: "calendar", assetKey: "screenCalendar", title: "School calendar", role: "Student view", group: "featured", icon: CalendarDays,
    description: "Exams, meetings and fee dates are grouped into a calm upcoming view with timing and venue context.",
    evidence: "The captured view shows the next 30 days and labels the mobile calendar as read-only.",
  },
  {
    key: "notices", assetKey: "screenNotices", title: "Notices", role: "Student view", group: "featured", icon: FileText,
    description: "Pinned circulars and category labels make school-wide updates easier to prioritise.",
    evidence: "The notice list preserves event, holiday and audience context without crowding the reading view.",
  },
  {
    key: "security", assetKey: "screenSecurityOverview", title: "Account security", role: "Staff and leaders", group: "featured", icon: LockKeyhole,
    description: "Password, authenticator and notification-device controls make account protection visible and actionable.",
    evidence: "The overview separates password, authenticator-app and notification-device controls so each can be reviewed deliberately.",
  },
]);

const FEATURED_MODULES = MOBILE_MODULES;

const SEO_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Vidyapeeth360 Mobile App",
  applicationCategory: "EducationalApplication",
  operatingSystem: "Android, iOS",
  url: `${seoOrigin}/mobile-app`,
  description: "A role-aware mobile experience for students, guardians, teachers and school leaders.",
};

function usePageMotionOff() {
  const reducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  return Boolean(reducedMotion || paused);
}

function Reveal({ children, className = "", delay = 0, ...props }) {
  const motionOff = usePageMotionOff();
  const visible = { opacity: 1, transform: "translate3d(0, 0, 0)" };
  return (
    <m.div
      className={className}
      data-motion-reveal="true"
      initial={motionOff ? false : { opacity: 0, transform: "translate3d(0, 16px, 0)" }}
      animate={motionOff ? visible : undefined}
      whileInView={motionOff ? undefined : visible}
      viewport={motionOff ? undefined : { once: true, amount: 0.16 }}
      transition={{ duration: motionOff ? 0 : 0.38, delay: motionOff ? 0 : delay, ease: EASE }}
      {...props}
    >
      {children}
    </m.div>
  );
}

function ProductPhone({ assetKey, alt, priority = false, className = "", testId }) {
  return (
    <figure className={className} data-testid={testId}>
      <div className="relative rounded-[2rem] border border-line-strong bg-[var(--vp-hero-navy)] p-2 shadow-[0_28px_70px_-28px_rgba(10,42,91,0.6)]">
        <span className="pointer-events-none absolute left-1/2 top-3 z-20 h-1.5 w-12 -translate-x-1/2 rounded-full bg-[var(--vp-hero-navy)]" aria-hidden="true" />
        <MarketingAssetSlot
          assetKey={assetKey}
          alt={alt}
          priority={priority}
          fit="contain"
          sizes="201px"
          className="!rounded-[1.48rem] !border-0 bg-white"
        />
      </div>
    </figure>
  );
}

function ProductDisclosure({ className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 ${className}`}>
      <span className="inline-flex min-h-8 items-center gap-2 rounded-full border border-positive/25 bg-surface px-3 text-[10px] font-bold uppercase tracking-[0.13em] text-positive shadow-card">
        <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
        Real product UI
      </span>
      <span className="text-xs font-semibold text-ink-subtle">First-party captures <span aria-hidden="true">·</span> synthetic sample data</span>
    </div>
  );
}

function HeroPhones() {
  const motionOff = usePageMotionOff();
  const enter = (x, y, delay) => ({
    initial: motionOff ? false : {
      opacity: 0,
      transform: `translate3d(${x}px, ${y}px, 0) scale(0.985)`,
    },
    animate: { opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" },
    transition: { duration: motionOff ? 0 : 0.48, delay: motionOff ? 0 : delay, ease: EASE },
  });

  return (
    <div
      className="relative mx-auto h-[31rem] w-full max-w-[34rem] sm:h-[35rem]"
      role="group"
      aria-label="Three screens from the Vidyapeeth360 mobile app"
      data-motion-state={motionOff ? "off" : "ready"}
    >
      <div className="pointer-events-none absolute inset-0 grid place-items-center" aria-hidden="true">
        <div
          data-testid="hero-phone-aura"
          className="h-[19rem] w-[19rem] rounded-full border border-primary/15 bg-primary-50 sm:h-[24rem] sm:w-[24rem]"
        />
      </div>

      <div data-testid="hero-phone-guardian-layout" className="absolute left-0 top-20 hidden w-[11.5rem] -rotate-[5deg] origin-bottom sm:block">
        <m.div {...enter(-22, 18, 0.12)}>
          <ProductPhone assetKey="screenTaskDetail" alt="Operations task screen showing a sample lab-safety checklist and photo-proof controls." testId="hero-phone-guardian" />
        </m.div>
      </div>
      <div data-testid="hero-phone-teacher-layout" className="absolute right-0 top-20 hidden w-[11.5rem] rotate-[5deg] origin-bottom sm:block">
        <m.div {...enter(22, 18, 0.18)}>
          <ProductPhone assetKey="screenSecurityOverview" alt="Account security screen showing password, authenticator and notification-device controls." testId="hero-phone-teacher" />
        </m.div>
      </div>
      <div data-testid="hero-phone-student-layout" className="absolute left-1/2 top-0 z-10 w-[12.5625rem] max-w-[76vw] -translate-x-1/2">
        <m.div {...enter(0, 24, 0.06)}>
          <ProductPhone assetKey="screenAira" alt="Aira assistant screen showing sample prompts for fees, attendance, marks and transport." priority testId="hero-phone-student" />
        </m.div>
      </div>
      <ProductDisclosure className="absolute inset-x-0 bottom-0 justify-center px-4" />
    </div>
  );
}

function TabRow({ items, activeKey, onChange, label, testPrefix }) {
  const activateFromKeyboard = (event, index) => {
    const handled = ["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key);
    if (!handled) return;
    event.preventDefault();
    let nextIndex = index;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % items.length;
    if (event.key === "ArrowLeft") nextIndex = (index - 1 + items.length) % items.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = items.length - 1;
    const next = items[nextIndex];
    onChange(next.key);
    document.getElementById(`${testPrefix}-tab-${next.key}`)?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {items.map((item, index) => {
        const Icon = item.icon;
        const active = item.key === activeKey;
        return (
          <button
            key={item.key}
            id={`${testPrefix}-tab-${item.key}`}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`${testPrefix}-panel`}
            tabIndex={active ? 0 : -1}
            data-testid={`${testPrefix}-${item.key}`}
            onClick={() => onChange(item.key)}
            onKeyDown={(event) => activateFromKeyboard(event, index)}
            className={`inline-flex min-h-[46px] shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-[background-color,border-color,color,transform] duration-fast active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/60 ${active ? "border-primary bg-primary text-primary-fg" : "border-line bg-surface text-ink-2 hover:border-primary/40 hover:text-ink"}`}
          >
            {Icon && <Icon className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />}
            {item.title}
          </button>
        );
      })}
    </div>
  );
}

function RoleSwitcher() {
  const motionOff = usePageMotionOff();
  const [activeKey, setActiveKey] = useState(MOBILE_PERSONAS[0].key);
  const active = MOBILE_PERSONAS.find(({ key }) => key === activeKey) || MOBILE_PERSONAS[0];

  return (
    <section className="border-y border-line bg-muted" aria-labelledby="roles-title">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:py-28">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">Role-aware by design</p>
          <h2 id="roles-title" className="mt-4 max-w-[15ch] font-heading text-3xl font-extrabold leading-[1.08] tracking-tight text-ink sm:text-4xl lg:text-5xl">
            The same school. A clearer path for every audience.
          </h2>
          <p className="mt-5 max-w-[62ch] text-base leading-relaxed text-ink-subtle">
            Each role opens on the action it needs next, with the same permissions the web platform enforces. These workflows are in controlled early access.
          </p>
        </Reveal>

        <Reveal className="mt-9" delay={0.04}>
          <TabRow
            items={MOBILE_PERSONAS}
            activeKey={activeKey}
            onChange={setActiveKey}
            label="Choose a school role"
            testPrefix="mobile-role"
          />
        </Reveal>

        <Reveal
          id="mobile-role-panel"
          role="tabpanel"
          aria-labelledby={`mobile-role-tab-${active.key}`}
          className="mt-10 grid min-w-0 gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(18rem,0.58fr)] lg:items-center"
          delay={0.08}
        >
          <m.div
            key={`${active.key}-copy`}
            initial={motionOff ? false : { opacity: 0, transform: "translate3d(0, 8px, 0)" }}
            animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
            transition={{ duration: motionOff ? 0 : 0.22, ease: EASE }}
          >
            <p className="text-sm font-semibold text-orange-text">{active.label}</p>
            <h3 className="mt-3 font-heading text-3xl font-bold tracking-tight text-ink">{active.title}</h3>
            <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-ink-subtle">{active.description}</p>
            <ul className="mt-7 space-y-4">
              {active.proofs.map((proof) => (
                <li key={proof} className="flex max-w-[56ch] items-start gap-3 text-sm leading-relaxed text-ink-2">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-positive-soft text-positive">
                    <Check className="h-3.5 w-3.5" strokeWidth={2.2} aria-hidden="true" />
                  </span>
                  {proof}
                </li>
              ))}
            </ul>
          </m.div>
          <m.div
            key={active.key}
            initial={motionOff ? false : { opacity: 0, transform: "translate3d(0, 10px, 0) scale(0.985)" }}
            animate={{ opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" }}
            transition={{ duration: motionOff ? 0 : 0.22, ease: EASE }}
            className="mx-auto w-full max-w-[12.5625rem]"
          >
            <ProductPhone
              assetKey={active.assetKey}
              alt={`${active.title} workflow shown in a first-party Vidyapeeth360 mobile capture with synthetic sample data.`}
              testId="active-role-phone"
            />
            <ProductDisclosure className="mt-4 justify-center" />
          </m.div>
        </Reveal>
      </div>
    </section>
  );
}

function ModuleExplorer() {
  const motionOff = usePageMotionOff();
  const [activeKey, setActiveKey] = useState(FEATURED_MODULES[0].key);
  const active = MOBILE_MODULES.find(({ key }) => key === activeKey) || FEATURED_MODULES[0];

  return (
    <section className="border-y border-line bg-primary-50 text-ink" aria-labelledby="modules-title">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:py-28">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-orange-text">Connected module journeys</p>
          <h2 id="modules-title" className="mt-4 max-w-[15ch] font-heading text-3xl font-extrabold leading-[1.08] tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Follow the mobile workflow.
          </h2>
          <p className="mt-5 max-w-[62ch] text-base leading-relaxed text-ink-subtle">
            Pick a workflow to see who it is for, what it shows and what happens next.
          </p>
          <ProductDisclosure className="mt-5" />
        </Reveal>

        <Reveal className="mt-9 flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="group" aria-label="Featured mobile modules" delay={0.04}>
          {FEATURED_MODULES.map((module) => {
            const Icon = module.icon;
            const activeTab = active.key === module.key;
            return (
              <button
                key={module.key}
                type="button"
                aria-pressed={activeTab}
                aria-controls="mobile-module-panel"
                data-testid={`mobile-module-${module.key}`}
                onClick={() => setActiveKey(module.key)}
                className={`inline-flex min-h-[46px] shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-[background-color,border-color,color,transform] duration-fast active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/60 ${activeTab ? "border-primary bg-primary text-primary-fg" : "border-line bg-surface text-ink-2 hover:border-primary/40 hover:text-ink"}`}
              >
                <Icon className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
                {module.title}
              </button>
            );
          })}
        </Reveal>

        <Reveal id="mobile-module-panel" aria-live="polite" className="mt-10 grid min-w-0 gap-12 lg:grid-cols-[minmax(18rem,0.68fr)_minmax(0,1fr)] lg:items-center" delay={0.08}>
          <m.div
            key={active.key}
            data-testid="active-phone"
            data-asset-key-active={active.assetKey}
            initial={motionOff ? false : { opacity: 0, transform: "translate3d(0, 10px, 0) scale(0.985)" }}
            animate={{ opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" }}
            transition={{ duration: motionOff ? 0 : 0.22, ease: EASE }}
            className="mx-auto w-full max-w-[12.5625rem] lg:sticky lg:top-[128px]"
          >
            <ProductPhone
              assetKey={active.assetKey}
              alt={`${active.title} screen in the Vidyapeeth360 mobile app, shown with synthetic sample data.`}
            />
          </m.div>

          <m.div
            key={`${active.key}-module-copy`}
            initial={motionOff ? false : { opacity: 0, transform: "translate3d(0, 8px, 0)" }}
            animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
            transition={{ duration: motionOff ? 0 : 0.22, ease: EASE }}
            className="min-w-0"
          >
            <p className="text-sm font-semibold text-orange-text">{active.role}</p>
            <h3 className="mt-3 max-w-[14ch] font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl">{active.title}</h3>
            <p className="mt-5 max-w-[48ch] text-lg leading-relaxed text-ink-2">{active.description}</p>
            <div className="mt-8 border-t border-line pt-6">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink-subtle">How the workflow behaves</p>
              <p className="mt-3 max-w-[54ch] text-sm leading-relaxed text-ink-subtle">{active.evidence}</p>
            </div>
            <div className="mt-8 flex items-center gap-3 text-sm font-semibold text-ink-2">
              <span className="grid h-9 w-9 place-items-center rounded-full border border-primary/20 bg-surface text-primary">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              </span>
              Role-scoped access follows the signed-in account.
            </div>
          </m.div>
        </Reveal>

      </div>
    </section>
  );
}

const STAFF_STORIES = [
  {
    icon: ClipboardCheck,
    title: "Operations in the field",
    description: "Priority tasks, typed checklists, QR scanning and proof capture for campus and transport teams — a submission keeps working through a network drop and syncs safely.",
  },
  {
    icon: Presentation,
    title: "Teacher classroom toolkit",
    description: "Day plan, sections, timetable, attendance, homework and marks stay together without turning the phone into a dense desktop.",
  },
  {
    icon: WalletCards,
    title: "Staff self-service",
    description: "Leave, published payslips, Form 16 summaries and masked salary-account details stay available to the employee who owns them.",
  },
  {
    icon: Building2,
    title: "Leadership command centre",
    description: "Fee collection, students at risk, finance figures and the approvals waiting on you — the numbers a head needs before deciding, on a phone.",
  },
];

function StaffBreadth() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-20 lg:py-28" aria-labelledby="staff-title">
      <Reveal className="grid gap-7 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">More than a family companion</p>
          <h2 id="staff-title" className="mt-4 max-w-[15ch] font-heading text-3xl font-extrabold leading-[1.08] tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Useful when the school day gets operational.
          </h2>
        </div>
        <p className="max-w-[48ch] text-base leading-relaxed text-ink-subtle lg:col-span-5">
          Staff and leaders get focused mobile tools for the work that cannot wait for a desk.
        </p>
      </Reveal>

      <div className="mt-12 grid border-y border-line sm:grid-cols-2 lg:grid-cols-4">
        {STAFF_STORIES.map((story, index) => {
          const Icon = story.icon;
          return (
            <Reveal key={story.title} delay={index * 0.035} className="border-b border-line py-7 sm:px-6 sm:[&:nth-child(odd)]:border-r lg:border-b-0 lg:border-r lg:first:pl-0 lg:last:border-r-0 lg:last:pr-0">
              <Icon className="h-6 w-6 text-orange-text" strokeWidth={1.8} aria-hidden="true" />
              <h3 className="mt-5 font-heading text-lg font-bold leading-snug text-ink">{story.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-subtle">{story.description}</p>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

const TRUST_POINTS = [
  {
    icon: ShieldCheck,
    title: "Role-scoped access",
    description: "A guardian sees their own children, while teachers work within assigned sections. Access is checked by the server, not merely hidden in the interface.",
  },
  {
    icon: LockKeyhole,
    title: "Accountable records",
    description: "Protected downloads require an authenticated request, and review-only family records remain visibly read-only on mobile.",
  },
  {
    icon: Languages,
    title: "Usable by everyone on your staff",
    description: "Comfortable touch targets, theme choices, persisted language settings and reduced-motion-aware interactions support different ways of using the app.",
  },
];

function TrustProof() {
  return (
    <section className="border-y border-line bg-muted" aria-labelledby="trust-title">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:py-28">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">Trust is part of the workflow</p>
          <h2 id="trust-title" className="mt-4 max-w-[16ch] font-heading text-3xl font-extrabold leading-[1.08] tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Calm on the surface. Accountable underneath.
          </h2>
        </Reveal>

        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {TRUST_POINTS.map((point, index) => {
            const Icon = point.icon;
            return (
              <Reveal key={point.title} delay={index * 0.04} className="border-t border-line pt-6">
                <Icon className="h-6 w-6 text-orange-text" strokeWidth={1.8} aria-hidden="true" />
                <h3 className="mt-5 font-heading text-xl font-bold tracking-tight text-ink">{point.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-subtle">{point.description}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function MobileHero({ onDemo }) {
  const motionOff = usePageMotionOff();
  return (
    <section className="mx-auto grid max-w-7xl gap-12 px-5 pb-14 pt-14 lg:grid-cols-12 lg:items-center lg:pb-20 lg:pt-20" aria-labelledby="mobile-app-title">
      <m.div
        className="min-w-0 lg:col-span-5"
        data-motion-reveal="hero-copy"
        initial={motionOff ? false : { opacity: 0, transform: "translate3d(0, 16px, 0)" }}
        animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
        transition={{ duration: motionOff ? 0 : 0.44, ease: EASE }}
      >
        <p className="inline-flex min-h-8 items-center rounded-full border border-orange-border bg-orange-soft px-3 text-xs font-bold uppercase tracking-[0.14em] text-orange-text">
          A premium mobile experience
        </p>
        <h1 id="mobile-app-title" className="mt-6 max-w-[11ch] font-heading text-4xl font-extrabold leading-[1.02] tracking-[-0.035em] text-ink sm:text-5xl lg:text-[3.75rem]">
          School days, made clear.
        </h1>
        <p className="mt-6 max-w-[45ch] text-base leading-relaxed text-ink-subtle sm:text-lg">
          One connected app for students, guardians, teachers and school leaders — shaped around the next useful action, not a desktop squeezed onto a phone.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={onDemo}
            data-testid="mobileapp-request-demo"
            className="mobile-hover-lift group inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-fg shadow-elevated transition-[background-color,transform,box-shadow] duration-fast hover:bg-primary-700 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            Book a demo
            <ArrowRight className="h-4 w-4 transition-transform duration-fast group-hover:translate-x-0.5" aria-hidden="true" />
          </button>
          <Link to="/register" className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-line bg-surface px-6 text-sm font-semibold text-ink transition-[background-color,border-color,transform] duration-fast hover:border-primary/40 hover:bg-muted active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/60">
            {CTA_PRIMARY_LABEL}
          </Link>
        </div>
        <p className="mt-5 max-w-[48ch] text-xs leading-relaxed text-ink-subtle">
          Native apps are in controlled early access; public store availability follows school review and staged rollout.
        </p>
      </m.div>

      <div className="min-w-0 lg:col-span-7">
        <HeroPhones />
      </div>
    </section>
  );
}

function MobileSummary() {
  const summaries = [
    ["Audience pathways", "4 focused views"],
    ["Screen evidence", "5 product screens"],
    ["Access model", "One permission system"],
  ];
  return (
    <section className="border-y border-line bg-surface" aria-label="Mobile experience summary">
      <dl className="mx-auto grid max-w-7xl divide-y divide-line px-5 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {summaries.map(([term, detail], index) => (
          <Reveal
            key={term}
            delay={index * 0.05}
            className={`py-5 sm:px-6 ${index === 0 ? "sm:pl-0" : ""} ${index === summaries.length - 1 ? "sm:pr-0" : ""}`}
          >
            <dt className="text-xs font-semibold uppercase tracking-[0.12em] text-ink-subtle">{term}</dt>
            <dd className="mt-1 font-heading text-2xl font-bold text-ink">{detail}</dd>
          </Reveal>
        ))}
      </dl>
    </section>
  );
}

function ConversionClose({ onDemo }) {
  return (
    <section className="mx-auto max-w-7xl px-5 py-20 lg:py-28" aria-labelledby="mobile-cta-title">
      <Reveal className="overflow-hidden rounded-[2rem] border border-line bg-surface px-6 py-12 shadow-elevated sm:px-10 lg:grid lg:grid-cols-12 lg:items-end lg:px-14 lg:py-16">
        <div className="lg:col-span-8">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-orange-text">Bring the school day closer</p>
          <h2 id="mobile-cta-title" className="mt-4 max-w-[15ch] font-heading text-3xl font-extrabold leading-[1.08] tracking-tight text-ink sm:text-4xl lg:text-5xl">
            See the app through your school’s workflows.
          </h2>
          <p className="mt-5 max-w-[54ch] text-base leading-relaxed text-ink-subtle">
            We’ll walk through the roles and modules that matter to your team, then connect them to the wider <BrandWordmarkText /> platform.
          </p>
        </div>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:col-span-4 lg:mt-0 lg:justify-end">
          <button type="button" onClick={onDemo} className="group inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-fg transition-[background-color,transform] duration-fast hover:bg-primary-700 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-surface">
            Book a demo
            <ArrowRight className="h-4 w-4 transition-transform duration-fast group-hover:translate-x-0.5" aria-hidden="true" />
          </button>
          <Link to="/register" className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-line bg-surface px-6 text-sm font-semibold text-ink transition-[background-color,border-color,transform] duration-fast hover:border-primary/40 hover:bg-muted active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/60">
            {CTA_PRIMARY_LABEL}
          </Link>
        </div>
      </Reveal>
    </section>
  );
}

export default function MobileAppShowcase() {
  const [salesOpen, setSalesOpen] = useState(false);

  const openDemo = (placement) => {
    track("demo_request", { placement, context: "mobile_app", page: "/mobile-app" });
    setSalesOpen(true);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <MarketingMotion>
      <div className="landing-page flex min-h-[100dvh] flex-col overflow-x-clip bg-canvas text-ink" data-testid="mobile-app-showcase">
        <MarketingSeo
          title="Early-Access School Mobile App | Vidyapeeth360"
          description="See what parents, students, teachers and school leaders can already do in the Vidyapeeth360 app — controlled early access, before public release."
          path="/mobile-app"
          image="/og/og-mobile-app.jpg"
          schema={SEO_SCHEMA}
        />
        <ScrollProgressBar />
      <MarketingTopBar onBookDemo={() => setSalesOpen(true)} />

        <main className="flex-1">
          <MobileHero onDemo={() => openDemo("mobile_app_hero")} />
          <MobileSummary />

          <RoleSwitcher />
          <ModuleExplorer />
          <StaffBreadth />
          <TrustProof />
          <ConversionClose onDemo={() => openDemo("mobile_app_close")} />
        </main>

        <SiteFooter />
        <ContactSalesModal open={salesOpen} onClose={() => setSalesOpen(false)} contextLabel="the parent, student and staff mobile experience" placement="mobile_app_page" />
      </div>
    </MarketingMotion>
  );
}
